import React, { useState } from 'react';
import BoutonAide from './charte/BoutonAide';
import { AIDE_RESUME } from '../aide/textesAide';

/* Résumé de mission produit par l'assistant, modifiable par le client avant
   l'envoi. Chaque champ porte une bulle d'aide. Aucun prix : le montant
   arrive avec le devis de Tribe. Le champ « produits », résolu côté serveur
   à partir du référentiel, n'est ni affiché ni modifié ici. */

export interface ResumeMission {
  titre: string;
  zone: string;
  description: string;
  typeDonnees: string[];
  champsCollecte: string[];
  produits?: string[];
  collecteursEstimes: number;
  delaiEstime: string;
  valeurAjoutee: string;
}

const texte = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const liste = (v: unknown) => (Array.isArray(v) ? v.map(texte).map(s => s.trim()).filter(Boolean) : []);

/** Résumé lu tel que le modèle l'a rendu : chaque champ ramené à son type, rien d'absent. */
export function normaliserResume(brut: any): ResumeMission {
  const n = Number(brut?.collecteursEstimes);
  return {
    titre: texte(brut?.titre).trim(),
    zone: texte(brut?.zone).trim(),
    description: texte(brut?.description).trim(),
    typeDonnees: liste(brut?.typeDonnees),
    champsCollecte: liste(brut?.champsCollecte),
    produits: liste(brut?.produits),
    collecteursEstimes: Number.isFinite(n) && n > 0 ? Math.round(n) : 1,
    delaiEstime: texte(brut?.delaiEstime).trim(),
    valeurAjoutee: texte(brut?.valeurAjoutee).trim(),
  };
}

/** Champs à compléter avant l'envoi, en clair. */
export function manquesResume(r: ResumeMission): string[] {
  const manques: string[] = [];
  if (!r.titre.trim()) manques.push('le titre');
  if (!r.zone.trim()) manques.push('la zone');
  if (!r.description.trim()) manques.push('la description');
  if (r.champsCollecte.length === 0) manques.push('au moins une information à relever');
  return manques;
}

/* Présentation de la maquette NouvelleMission3 : une carte, un champ par
   case, étiquette en capitales mono, saisie sur fond transparent soulignée en
   pointillé ; chaque champ garde sa bulle d'aide. */

const Libelle: React.FC<{ pour: string; aide: string; children: React.ReactNode }> = ({ pour, aide, children }) => (
  <div className="ligne" style={{ gap: '6px' }}>
    <label htmlFor={pour} className="resume__libelle">{children}</label>
    <BoutonAide texte={aide} />
  </div>
);

const ListeModifiable: React.FC<{
  id: string;
  libelle: string;
  aide: string;
  valeurs: string[];
  onChange: (v: string[]) => void;
  exemple: string;
}> = ({ id, libelle, aide, valeurs, onChange, exemple }) => {
  const [saisie, setSaisie] = useState('');
  const ajouter = () => {
    const v = saisie.trim();
    if (!v || valeurs.some(x => x.toLowerCase() === v.toLowerCase())) { setSaisie(''); return; }
    onChange([...valeurs, v]);
    setSaisie('');
  };
  return (
    <>
      <Libelle pour={id} aide={aide}>{libelle}</Libelle>
      {valeurs.length > 0 && (
        <ul className="ligne" style={{ listStyle: 'none', gap: '6px' }} aria-label={`${libelle} : déjà retenues`}>
          {valeurs.map(v => (
            <li key={v} className="etiquette-type" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              {v}
              <button type="button" className="message__fermer" style={{ fontSize: '15px' }}
                aria-label={`Retirer « ${v} »`} onClick={() => onChange(valeurs.filter(x => x !== v))}>×</button>
            </li>
          ))}
        </ul>
      )}
      <div className="ligne" style={{ gap: '8px', flexWrap: 'nowrap' }}>
        <input id={id} className="resume__saisie" value={saisie} onChange={e => setSaisie(e.target.value)} placeholder={exemple}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }} />
        <button type="button" className="bouton bouton--secondaire bouton--petit" onClick={ajouter} disabled={!saisie.trim()}>Ajouter</button>
      </div>
    </>
  );
};

const ResumeModifiable: React.FC<{ resume: ResumeMission; onChange: (r: ResumeMission) => void }> = ({ resume, onChange }) => {
  const maj = <K extends keyof ResumeMission>(cle: K, v: ResumeMission[K]) => onChange({ ...resume, [cle]: v });

  return (
    <div className="resume">
      <div className="resume__case">
        <Libelle pour="resume-titre" aide={AIDE_RESUME.titre}>Titre de la mission</Libelle>
        <input id="resume-titre" className="resume__saisie resume__saisie--titre" value={resume.titre}
          onChange={e => maj('titre', e.target.value)} maxLength={150} />
      </div>

      <div className="resume__grille">
        <div className="resume__case">
          <Libelle pour="resume-zone" aide={AIDE_RESUME.zone}>Zone</Libelle>
          <input id="resume-zone" className="resume__saisie" value={resume.zone} onChange={e => maj('zone', e.target.value)}
            maxLength={120} placeholder="Ex. : Pikine" />
        </div>
        <div className="resume__case">
          <ListeModifiable id="resume-types" libelle="Type de lieu" aide={AIDE_RESUME.typeDonnees}
            valeurs={resume.typeDonnees} onChange={v => maj('typeDonnees', v)} exemple="Ex. : Pharmacies" />
        </div>
        <div className="resume__case">
          <Libelle pour="resume-spotters" aide={AIDE_RESUME.collecteursEstimes}>Spotters à mobiliser (estimation)</Libelle>
          <input id="resume-spotters" className="resume__saisie" type="number" min={1} max={500} inputMode="numeric" value={resume.collecteursEstimes}
            onChange={e => maj('collecteursEstimes', Math.max(1, Math.min(500, Number(e.target.value) || 1)))} />
        </div>
        <div className="resume__case">
          <Libelle pour="resume-delai" aide={AIDE_RESUME.delaiEstime}>Délai souhaité</Libelle>
          <input id="resume-delai" className="resume__saisie" value={resume.delaiEstime} onChange={e => maj('delaiEstime', e.target.value)}
            maxLength={80} placeholder="Ex. : 2 semaines" />
        </div>
      </div>

      <div className="resume__case">
        <ListeModifiable id="resume-champs" libelle="Informations à relever sur chaque lieu" aide={AIDE_RESUME.champsCollecte}
          valeurs={resume.champsCollecte} onChange={v => maj('champsCollecte', v)} exemple="Ex. : Horaires d'ouverture" />
      </div>

      <div className="resume__case">
        <Libelle pour="resume-description" aide={AIDE_RESUME.description}>Description</Libelle>
        <textarea id="resume-description" className="resume__saisie" rows={4} value={resume.description}
          onChange={e => maj('description', e.target.value)} />
      </div>

      <div className="resume__case">
        <Libelle pour="resume-valeur" aide={AIDE_RESUME.valeurAjoutee}>Ce que la mission doit vous apporter</Libelle>
        <textarea id="resume-valeur" className="resume__saisie" rows={2} value={resume.valeurAjoutee}
          onChange={e => maj('valeurAjoutee', e.target.value)} placeholder="Pourquoi cette mission, pour qui, contraintes particulières…" />
      </div>
    </div>
  );
};

export default ResumeModifiable;
